import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })
    }
    return apiInstance
}

// ============================================================================
// Type Definitions
// ============================================================================

export interface StashedInfoCreate {
    first_name: string
    last_name: string
    email: string
    product_id: string
    product_type: 'course' | 'bundle' | 'digital_product'
}

export interface StashedInfo {
    id: string
    first_name: string
    last_name: string
    email: string
    product_id: string
    product_type: 'course' | 'bundle' | 'digital_product'
    created_at: string
    updated_at: string
    // 24-hour follow-up
    follow_up_sent: boolean
    follow_up_sent_at?: string
    // 7-day follow-up
    follow_up_sent_7d: boolean
    follow_up_sent_7d_at?: string
    // 30-day follow-up
    follow_up_sent_30d: boolean
    follow_up_sent_30d_at?: string
    // Completion
    completed: boolean
    completed_at?: string
}

// ============================================================================
// Service Functions
// ============================================================================

export const stashedInfoService = {
    createStashedInfo,
    markAsCompleted,
}

/**
 * Create or update stashed enrollment information
 * This is called when a user fills out the checkout form but hasn't completed purchase
 */
async function createStashedInfo(stashedInfo: StashedInfoCreate): Promise<ApiResponse<StashedInfo>> {
    try {
        const response = await getApiInstance().post('/stashed-info/', stashedInfo)
        const data = response.data
        
        return {
            success: true,
            data: data
        }
    } catch (error: any) {
        console.error('Error creating stashed info:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to save information'
        }
    }
}

/**
 * Mark a stashed enrollment as completed
 * This should be called after successful enrollment
 */
async function markAsCompleted(email: string, productId: string): Promise<ApiResponse<{ success: boolean }>> {
    try {
        const response = await getApiInstance().patch('/stashed-info/mark-completed', null, {
            params: { email, product_id: productId }
        })
        const data = response.data
        
        return {
            success: true,
            data: data
        }
    } catch (error: any) {
        console.error('Error marking stashed info as completed:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to mark as completed'
        }
    }
}

export default stashedInfoService